import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DataTable, DatePicker, Drawer, Select, toast } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { Button, Switch, Typography, Box } from "@mui/material";
import { Inline } from "components/layout/reusableLayouts";
import EmployeeSearchAutoComplete, {
  Employee,
} from "../../common/EmployeeSearchAutoComplete";
import { UserIcon, CreditCardIcon } from "@heroicons/react/24/outline";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { GET_ACCOUNT } from "../../../graphql/account";
import { ADD_EXTERNAL_CARDS } from "../../../graphql/card";
import dayjs from "utils/dayjs";
import { fullName } from "utils/fullName";

// Heroicons ship no width/height, so the size has to come from the call site.
const ICON_STYLE = {
  width: 20,
  height: 20,
  color: "var(--color-text-brand)",
};

interface MapUserExternalCardProps {
  accountId: string | null;
  cards: Card[];
  open: boolean;
  isEdit: boolean;
  onSuccess: () => void;
  onExit: () => void;
}

export interface Card {
  loadCard: boolean;
  cardIssuerId: string;
  nickname: string;
  cardNumber: string;
  ownerName: string;
  ownerEmail: string;
  user: User;
  possibleCards: [PossibleCard] | null;
  selectedCard: string;
}

interface PossibleCard {
  id: string;
  nickname: string;
  cardNumber: string;
  status: string;
  user: User;
}

interface User {
  id: string;
  firstName: string;
  lastName?: string | null;
}

const NEW_CARD = "New";

const MapUserExternalCardDrawer: React.FC<MapUserExternalCardProps> = (
  props,
) => {
  const { accountId, cards, open, isEdit, onSuccess, onExit } = props;
  const { t } = useTranslation();
  const [mappedCards, setMappedCards] = React.useState<Card[]>([]);
  const [syncStartDate, setSyncStartDate] = React.useState<number | null>(
    dayjs().startOf("day").valueOf(),
  );
  const [account, setAccount] = React.useState<any>(null);

  const [getAccount, { loading: accountLoading }] = useLazyQuery(GET_ACCOUNT, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      setAccount(data.account);
    },
    onError: (error) => {
      toast.error(error.message || t("Error getting account"));
    },
  });
  const [addExternalCards, { loading }] = useMutation(ADD_EXTERNAL_CARDS);

  const handleSaveCards = () => {
    if (mappedCards.length === 0) {
      onExit();
      return;
    }
    const isMapped = mappedCards
      .filter((card) => card.loadCard)
      .every((card) => card?.user?.id || card.selectedCard);
    if (!isMapped) {
      toast.error(
        t("Please validate that you have assigned a user to each card"),
      );
      return;
    }
    if (!syncStartDate) {
      toast.error(t("Missing start date for syncing cards"));
      return;
    }
    if (dayjs().diff(syncStartDate, "days") > 30) {
      toast.error(t("Start date must be within the last 30 days"));
      return;
    }

    addExternalCards({
      variables: {
        accountId: accountId,
        startDate: syncStartDate,
        input: mappedCards
          .map((card) => {
            if (!card?.loadCard) return;
            return {
              cardIssuerId: card.cardIssuerId,
              selectedCardId: card.selectedCard,
              userId: card.user.id,
            };
          })
          .filter((card) => card),
      },
      onCompleted: (data) => {
        toast.success(t("External cards saved successfully"));
        onSuccess();
      },
      onError: (error) => {
        toast.error(error.message || t("Error saving external cards"));
      },
    });
  };

  const handleSelectCard = (cardId: string, idx: number) => {
    if (!cardId) return;

    setMappedCards((prevMappedCards) => {
      const updatedCards = [...prevMappedCards];
      const possibleCard = updatedCards[idx].possibleCards?.find(
        (card) => card.id === cardId,
      );
      updatedCards[idx] = {
        ...updatedCards[idx],
        user: {
          id: possibleCard?.user?.id || "",
          firstName: possibleCard?.user?.firstName || "",
          lastName: possibleCard?.user?.lastName || "",
        },
        // "New Card" is the no-card state; the server reads any truthy selectedCardId as a card to link.
        selectedCard: cardId === NEW_CARD ? "" : cardId,
      };
      return updatedCards;
    });
  };

  const handleSelectEmployee = (employee: Employee | null, idx: number) => {
    setMappedCards((prevMappedCards) => {
      const updatedCards = [...prevMappedCards];
      updatedCards[idx] = {
        ...updatedCards[idx],
        user: {
          id: employee?.id || "",
          firstName: employee?.firstName || "",
          lastName: employee?.lastName || "",
        },
        selectedCard: "",
      };
      return updatedCards;
    });
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) return;
    setSyncStartDate(date.startOf("day").valueOf());
  };

  const handleLoadCard = (loadCard: boolean, idx: number) => {
    setMappedCards((prevMappedCards) => {
      const updatedCards = [...prevMappedCards];
      updatedCards[idx] = {
        ...updatedCards[idx],
        loadCard: loadCard,
      };
      return updatedCards;
    });
  };

  useEffect(() => {
    setMappedCards(cards.map((card) => ({ ...card, loadCard: true })));
  }, [cards]);

  useEffect(() => {
    if (accountId) {
      getAccount({
        variables: {
          id: accountId,
        },
      });
    }
  }, [accountId]);

  const columns: DataColumn<Card>[] = [
    {
      header: t("Select"),
      cell: (card, idx) => (
        <Switch
          checked={card.loadCard}
          onChange={(event) => handleLoadCard(event.target.checked, idx)}
          slotProps={{ input: { "aria-label": card.cardNumber } }}
        />
      ),
    },
    {
      header: t("Card"),
      maxWidth: 192,
      cell: (card) => ({
        content: card.cardNumber,
        caption: card.ownerName || (card.nickname ? " | " + card.nickname : ""),
      }),
    },
    {
      header: t("Existing Cards"),
      width: 256,
      cell: (card, idx) =>
        card.possibleCards ? (
          <Select
            aria-label={t("Existing Cards")}
            size="md"
            fullWidth
            clearable={false}
            value={card.selectedCard || NEW_CARD}
            onChange={(cardId) => handleSelectCard(cardId ?? "", idx)}
            options={[
              { id: NEW_CARD, label: t("New Card") },
              ...card.possibleCards.map((possibleCard) => ({
                id: possibleCard.id,
                label: possibleCard.nickname + " | " + possibleCard.cardNumber,
                caption: fullName(possibleCard.user),
              })),
            ]}
          />
        ) : (
          t("New Card")
        ),
    },
    {
      header: t("User"),
      width: 256,
      cell: (card, idx) => (
        <EmployeeSearchAutoComplete
          title="Matched User"
          handleSelectEmployee={(employee) =>
            handleSelectEmployee(employee, idx)
          }
          // The pending-cards query fetches no email, which the picker only shows in its label.
          employee={card.user?.id ? { ...card.user, email: "" } : null}
        />
      ),
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={onExit}
      width={720}
      title={t("Map External Cards")}
      loading={loading || accountLoading}
      footer={
        <>
          <Button
            variant="primary-outlined"
            onClick={() => {
              setMappedCards([]);
              onExit();
            }}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="primary-filled"
            onClick={async () => {
              handleSaveCards();
            }}
          >
            {t("Save")}
          </Button>
        </>
      }
    >
      <>
        {account && !account?.startAt && (
          <>
            <Inline
              wrap={false}
              gap="var(--space-3)"
              sx={{ marginBottom: "var(--space-5)" }}
            >
              <CreditCardIcon style={ICON_STYLE} />
              <Typography variant="h3">{t("Start Date")}</Typography>
            </Inline>
            <Typography variant="body2" sx={{ marginBottom: "var(--space-3)" }}>
              {t("transactionSyncDateNotice")}
            </Typography>
            <Box sx={{ marginBottom: "var(--space-11)" }}>
              <DatePicker
                aria-label={t("Start Date")}
                size="medium"
                value={dayjs(syncStartDate)}
                onChange={(date) => handleDateChange(date)}
              />
            </Box>
          </>
        )}
        <Inline
          wrap={false}
          gap="var(--space-3)"
          sx={{ marginBottom: "var(--space-5)" }}
        >
          <UserIcon style={ICON_STYLE} />
          <Typography variant="h3">
            {t("External Card User Mapping")}
          </Typography>
        </Inline>
        <Typography variant="body2">
          {t("Review each card to ensure it is mapped to the correct user")}
        </Typography>

        <DataTable<Card>
          columns={columns}
          rows={accountId ? mappedCards : []}
          getRowKey={(card) => card.cardIssuerId}
          emptyState={
            account?.startAt
              ? t("No new cards found")
              : t("ExternalCardProcessing")
          }
        />
      </>
    </Drawer>
  );
};

export default MapUserExternalCardDrawer;
